import { ImageEffectType } from '@/typings/customilyElements/imageEffectType';
import type { SignedUrlResponse } from './typings/image';
import api from './baseApi';

const baseUrl = process.env.VUE_APP_CUSTOMILY_STOREFRONT_SERVICE_API_URL;

/**
 * Reference images have their own folder: they are only referenced from the effect config, so the
 * orphan-file cleaner that sweeps the preview images folder would delete them.
 */
export const getReferenceImageUploadSignedUrl = (filename: string): Promise<SignedUrlResponse> =>
    api
        .get<SignedUrlResponse>(`/ImageEffects/ReferenceImages/UploadSignedUrl/${encodeURIComponent(filename)}`)
        .then(({ data }) => data);

const removeBase64Prefix = (base64: string): string => {
    return base64.replace(/^data:image\/[^;]+;base64,/, '');
};

const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = removeBase64Prefix(result);
            resolve(base64);
        };
        reader.onerror = () => {
            reject(new Error('Failed to convert file to base64'));
        };
        reader.readAsDataURL(file);
    });
};

const postGenerateRequest = async (
    body: Record<string, unknown>
): Promise<{ url: string; metadata?: Record<string, unknown> }> => {
    const response = await fetch(`${baseUrl}/image/processing/generate`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const error = await response.json();
        let messageValue = '';

        try {
            const parsedMessage = JSON.parse(error.message) as {
                error: { message: string };
            };
            messageValue = parsedMessage?.error?.message;
        } catch {
            messageValue = error.message;
        }

        const message = `Failed to generate image effects: ${messageValue || response.statusText}`;
        throw new Error(message);
    }

    return await response.json();
};

export const generateImageEffects = async (data: {
    imageEffects: ImageEffectType[];
    username: string;
    file: File;
}): Promise<{ url: string; metadata?: Record<string, unknown> }> => {
    const { imageEffects, username, file } = data;

    const base64File = await convertFileToBase64(file);

    return postGenerateRequest({
        username: username,
        base64: base64File,
        imageEffects: imageEffects
    });
};

export const generateImageEffectsFromPrompt = async (data: {
    imageEffects: ImageEffectType[];
    username: string;
    prompt: string;
}): Promise<{ url: string; metadata?: Record<string, unknown> }> => {
    const { imageEffects, username, prompt } = data;

    return postGenerateRequest({
        username: username,
        prompt: prompt,
        imageEffects: imageEffects
    });
};
